import React, {
    createContext,
    useEffect,
    useState,
    useRef,
    useContext,
    ReactNode,
} from 'react';
import api from '../../services/api';
import { buildEndPoint, mapFiltersToOdata } from '../../utils/odataEndPoint';
import { readOnlyEntities } from '../../utils/commonVariables/entityKeys';
import {
    BookingListItem,
    BookingListViewType,
    BookingsFiltersState,
} from '../../types/booking/bookingListTypes';
import { adaptBookingsListToState } from '../../services/booking/bookingAdapters';
import { useIdHotelOwner } from '../../contexts/HotelOwnerContext';
import useTimeZone from '../../customHooks/useTimeZone';
import {
    defaultFilterTypes,
    sortDirections,
} from '../../types/lists/listTypes';

interface Message {
    type: 'info' | 'error' | '';
    message: string;
}

interface ContextBookingsType {
    items: BookingListItem[];
    filters: BookingsFiltersState;
    handleFilterChange: (
        key: keyof BookingsFiltersState,
        value: string | number | null | Date
    ) => void;
    sortDir: sortDirections;
    sortedBy: string;
    sortByField: (field: string) => void;
    handleSearch: (pageNumber: number) => void;
    handleClearFilters: () => void;
    isLoading: boolean;
    infoMessage: Message;
    handleInfoMessage: (type: 'info' | 'error' | '', message: string) => void;
    visibleView: BookingListViewType;
    handleChangeVisibleView: (view: BookingListViewType) => void;
    totalResults: number;
    page: number;
    currentResults: number;
    handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

export const ContextBookings = createContext<ContextBookingsType | undefined>(
    undefined
);

export const ProviderYourBookings = ({ children }: { children: ReactNode }) => {
    const timezone = useTimeZone();
    const IdHotelOwner = useIdHotelOwner();
    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState<BookingListItem[]>([]);

    const BOOKING_FILTERS_INITIAL_STATE: BookingsFiltersState = {
        IdStatus: {
            filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
            filterKey: 'SupplierBookingStatus/IdSupplierBookingStatusType',
            value: null,
        },
        BookingCode: {
            filterType: defaultFilterTypes.DEFAULT_STRING_FILTER_TYPE,
            filterKey: 'BookingCode',
            value: '',
        },
        IdSupplierHotel: {
            filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
            filterKey: 'IdSupplierHotel',
            value: null,
        },
        BookingDateFrom: {
            filterType: defaultFilterTypes.DEFAULT_DATE_TIME_FROM_FILTER_TYPE,
            filterKey: 'BookingDate',
            value: null,
            timeZone: timezone,
        },
        BookingDateTo: {
            filterType: defaultFilterTypes.DEFAULT_DATE_TIME_TO_FILTER_TYPE,
            filterKey: 'BookingDate',
            value: null,
            timeZone: timezone,
        },
        CheckInDateFrom: {
            filterType: defaultFilterTypes.DEFAULT_DATE_FROM_FILTER_TYPE,
            filterKey: 'CheckInDate',
            value: new Date(),
            timeZone: timezone,
        },
        CheckInDateTo: {
            filterType: defaultFilterTypes.DEFAULT_DATE_TO_FILTER_TYPE,
            filterKey: 'CheckInDate',
            value: null,
            timeZone: timezone,
        },
        CheckOutDateFrom: {
            filterType: defaultFilterTypes.DEFAULT_DATE_FROM_FILTER_TYPE,
            filterKey: 'CheckOutDate',
            value: null,
            timeZone: timezone,
        },
        CheckOutDateTo: {
            filterType: defaultFilterTypes.DEFAULT_DATE_TO_FILTER_TYPE,
            filterKey: 'CheckOutDate',
            value: null,
            timeZone: timezone,
        },
    };
    const [filters, setFilters] = useState<BookingsFiltersState>(
        BOOKING_FILTERS_INITIAL_STATE
    );

    const handleFilterChange = (
        key: keyof BookingsFiltersState,
        value: string | number | null | Date
    ) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [key]: {
                ...prevFilters[key],
                value,
            },
        }));
    };

    const [sortDir, setSortDir] = useState<sortDirections>(sortDirections.DESC);
    const [sortedBy, setSortedBy] = useState('CheckInDate');
    const handleSortByField = (field: string) => {
        if (field === sortedBy) {
            setSortDir(prevSortDir =>
                prevSortDir === sortDirections.ASC
                    ? sortDirections.DESC
                    : sortDirections.ASC
            );
        } else {
            setSortedBy(field);
            setSortDir(sortDirections.ASC);
        }
    };
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const INFO_MESSAGE_INITIAL_STATE: Message = { type: '', message: '' };
    const [infoMessage, setInfoMessage] = useState<Message>(
        INFO_MESSAGE_INITIAL_STATE
    );
    const [visibleView, setVisibleView] = useState(BookingListViewType.TABLE);
    const handleChangeVisibleView = (view: BookingListViewType) => {
        setVisibleView(view);
    };
    const handleInfoMessage = (
        type: 'info' | 'error' | '',
        message: string
    ) => {
        setInfoMessage({ type, message });
    };
    const itemsPerPage = 40;
    const currentResults = items.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);

    const active = useRef(true);

    const getBookingData = async (pageNumber = 1, filtersState = filters) => {
        setIsLoading(true);
        setPage(pageNumber);
        if (pageNumber === 1) setItems([]);
        const filtersToOdata = mapFiltersToOdata(filtersState);
        const endpoint = buildEndPoint(
            {
                entityName: readOnlyEntities.SupplierBooking,
                query: {
                    expand: [
                        readOnlyEntities.SupplierHotel,
                        readOnlyEntities.SupplierBookingStatus,
                        `${readOnlyEntities.SupplierBookingStatus}/${readOnlyEntities.SupplierBookingStatusType}`,
                    ],
                    select: [
                        'Id',
                        'SupplierBookingStatus/IdSupplierBookingStatusType',
                        'SupplierBookingStatus/SupplierBookingStatusType/Key',
                        'SupplierBookingStatus/SupplierBookingStatusType/Caption',
                        'SupplierBookingStatus/SupplierBookingStatusType/LocalizedCaption',
                        'SupplierBookingStatus/SupplierBookingStatusType/Color',
                        'BookingCode',
                        'BookingDate',
                        'CheckInDate',
                        'CheckOutDate',
                        'IdSupplierHotel',
                        'SupplierHotel/Name',
                    ],
                    filter: [
                        {
                            or: [
                                'SupplierBookingStatus/IdSupplierBookingStatusType eq 2',
                                'SupplierBookingStatus/IdSupplierBookingStatusType eq 3',
                            ],
                        },
                        'IdSupplierBookingRequest eq null',
                        ...filtersToOdata,
                    ],
                    orderBy: `${sortedBy} ${sortDir}`,
                    top: itemsPerPage,
                },
            },
            pageNumber
        );
        try {
            if (infoMessage.message) {
                setInfoMessage(INFO_MESSAGE_INITIAL_STATE);
            }
            const { data } = await api(endpoint);
            if (!active.current) return null;
            const _totalResults = parseInt(data['odata.count']);
            setTotalResults(_totalResults);
            if (!_totalResults) {
                setInfoMessage({
                    type: 'info',
                    message: 'no_results',
                });
            }
            const _items = adaptBookingsListToState(data.value);
            if (pageNumber === 1) {
                setItems(_items);
            } else {
                setItems(prevItems => [...prevItems, ..._items]);
            }
        } catch {
            setInfoMessage({
                type: 'error',
                message: 'an_error_occurred_while_processing_your_request',
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (IdHotelOwner) {
            handleSearch(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedBy, sortDir]);

    useEffect(() => {
        return () => {
            active.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (pageNumber: number) => {
        setPage(pageNumber);
        getBookingData(pageNumber);
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isOnBottom = scrollTop > scrollHeight - clientHeight - 1;
        if (isOnBottom && !isLoading && page < totalPages) {
            const nextPage = page + 1;
            setPage(nextPage);
            getBookingData(nextPage);
        }
    };
    const handleClearFilters = () => {
        setFilters(BOOKING_FILTERS_INITIAL_STATE);
        setPage(1);
        getBookingData(1, BOOKING_FILTERS_INITIAL_STATE);
    };

    return (
        <ContextBookings.Provider
            value={{
                items,
                filters,
                handleFilterChange,
                sortedBy,
                sortDir,
                sortByField: handleSortByField,
                isLoading,
                handleClearFilters,
                infoMessage,
                handleInfoMessage,
                visibleView,
                handleChangeVisibleView,
                totalResults,
                page,
                currentResults,
                handleScroll,
                handleSearch,
            }}
        >
            {children}
        </ContextBookings.Provider>
    );
};

export const useBookingList = () => {
    const context = useContext(ContextBookings);
    if (context === undefined) {
        throw new Error(
            'useBookingList must be used within a ContextBookings.Provider'
        );
    }
    return context;
};
